import { useState, useCallback, useRef, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';

type FraudType = 
  | 'PHOTO_OF_PHOTO'
  | 'MASK_DETECTED'
  | 'LIGHTING_INCONSISTENCY'
  | 'MOIRE_PATTERN'
  | 'REFLECTION_DETECTED';

interface FraudIndicator {
  type: FraudType;
  confidence: number;
  description: string;
}

interface AntiFraudResult {
  riskScore: number;
  indicators: FraudIndicator[];
  blocked: boolean;
  recommendation: 'APPROVE' | 'REVIEW' | 'REJECT';
}

interface AntiFraudAnalysisProps {
  imageData: string;
  onComplete: (result: AntiFraudResult) => void;
  onError?: (error: string) => void;
  className?: string;
}

const FRAUD_DESCRIPTIONS: Record<FraudType, string> = {
  PHOTO_OF_PHOTO: 'Possível foto de foto detectada',
  MASK_DETECTED: 'Possível uso de máscara detectado',
  LIGHTING_INCONSISTENCY: 'Inconsistência de iluminação detectada',
  MOIRE_PATTERN: 'Padrão moiré detectado (tela de dispositivo)',
  REFLECTION_DETECTED: 'Reflexo suspeito detectado',
};

export function AntiFraudAnalysis({
  imageData,
  onComplete,
  onError,
  className = '',
}: AntiFraudAnalysisProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AntiFraudResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentCheck, setCurrentCheck] = useState('');

  // Analyze image for moiré patterns (indicates photo of screen)
  const detectMoirePattern = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    
    // Convert to grayscale and detect high-frequency patterns
    let highFreqCount = 0;
    const threshold = 30;
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        // Check neighboring pixels for rapid intensity changes
        const neighbors = [
          ((y - 1) * width + x) * 4,
          ((y + 1) * width + x) * 4,
          (y * width + (x - 1)) * 4,
          (y * width + (x + 1)) * 4,
        ];
        
        let rapidChanges = 0;
        for (const nIdx of neighbors) {
          const nGray = (data[nIdx] + data[nIdx + 1] + data[nIdx + 2]) / 3;
          if (Math.abs(gray - nGray) > threshold) {
            rapidChanges++;
          }
        }
        
        if (rapidChanges >= 3) {
          highFreqCount++;
        }
      }
    }
    
    const totalPixels = (width - 2) * (height - 2);
    const moireRatio = highFreqCount / totalPixels;
    
    // High ratio indicates moiré pattern
    return Math.min(moireRatio * 10, 1);
  }, []);

  // Analyze lighting consistency
  const analyzeLighting = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    
    // Divide image into quadrants and compare average brightness
    const quadrants: number[] = [0, 0, 0, 0];
    const counts: number[] = [0, 0, 0, 0];
    
    const midX = width / 2;
    const midY = height / 2;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        let quadrant = 0;
        if (x >= midX) quadrant += 1;
        if (y >= midY) quadrant += 2;
        
        quadrants[quadrant] += brightness;
        counts[quadrant]++;
      }
    }
    
    // Calculate average brightness per quadrant
    const avgBrightness = quadrants.map((sum, i) => sum / counts[i]);
    
    // Calculate variance
    const mean = avgBrightness.reduce((a, b) => a + b, 0) / 4;
    const variance = avgBrightness.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / 4;
    
    // High variance indicates inconsistent lighting
    const normalizedVariance = Math.min(variance / 1000, 1);
    return normalizedVariance;
  }, []);

  // Detect reflections (bright spots)
  const detectReflections = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    
    let brightSpots = 0;
    const brightnessThreshold = 250;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        
        // Check for very bright pixels (potential reflections)
        if (r > brightnessThreshold && g > brightnessThreshold && b > brightnessThreshold) {
          brightSpots++;
        }
      }
    }
    
    const totalPixels = width * height;
    const reflectionRatio = brightSpots / totalPixels;
    
    // More than 5% bright spots is suspicious
    return Math.min(reflectionRatio * 20, 1);
  }, []);

  // Detect photo-of-photo (edges and frame detection)
  const detectPhotoOfPhoto = useCallback((imageData: ImageData): number => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    
    // Check for uniform borders (indicates photo frame)
    const borderSize = Math.min(width, height) * 0.05;
    let uniformBorderPixels = 0;
    let totalBorderPixels = 0;
    
    // Sample border pixels
    const sampleBorder = (startX: number, startY: number, endX: number, endY: number) => {
      const colors: number[] = [];
      for (let y = startY; y < endY; y += 5) {
        for (let x = startX; x < endX; x += 5) {
          const idx = (y * width + x) * 4;
          const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
          colors.push(gray);
          totalBorderPixels++;
        }
      }
      
      if (colors.length < 2) return 0;
      
      const mean = colors.reduce((a, b) => a + b, 0) / colors.length;
      const variance = colors.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / colors.length;
      
      // Low variance means uniform color (suspicious)
      if (variance < 100) {
        uniformBorderPixels += colors.length;
      }
    };
    
    // Check all four borders
    sampleBorder(0, 0, width, borderSize); // Top
    sampleBorder(0, height - borderSize, width, height); // Bottom
    sampleBorder(0, 0, borderSize, height); // Left
    sampleBorder(width - borderSize, 0, width, height); // Right
    
    const uniformRatio = totalBorderPixels > 0 ? uniformBorderPixels / totalBorderPixels : 0;
    return uniformRatio;
  }, []);

  const analyzeImage = useCallback(async () => {
    if (!canvasRef.current) return;

    setAnalyzing(true);
    setError(null);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // Load image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageData;
      });

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const indicators: FraudIndicator[] = [];
      let totalRisk = 0;

      // Check for moiré pattern
      setCurrentCheck('Verificando padrão moiré...');
      const moireScore = detectMoirePattern(imgData);
      if (moireScore > 0.3) {
        indicators.push({
          type: 'MOIRE_PATTERN',
          confidence: moireScore,
          description: FRAUD_DESCRIPTIONS.MOIRE_PATTERN,
        });
        totalRisk += moireScore * 30;
      }

      // Check lighting consistency
      setCurrentCheck('Analisando iluminação...');
      const lightingScore = analyzeLighting(imgData);
      if (lightingScore > 0.4) {
        indicators.push({
          type: 'LIGHTING_INCONSISTENCY',
          confidence: lightingScore,
          description: FRAUD_DESCRIPTIONS.LIGHTING_INCONSISTENCY,
        });
        totalRisk += lightingScore * 25;
      }

      // Check for reflections
      setCurrentCheck('Detectando reflexos...');
      const reflectionScore = detectReflections(imgData);
      if (reflectionScore > 0.3) {
        indicators.push({
          type: 'REFLECTION_DETECTED',
          confidence: reflectionScore,
          description: FRAUD_DESCRIPTIONS.REFLECTION_DETECTED,
        });
        totalRisk += reflectionScore * 20;
      }

      // Check for photo-of-photo
      setCurrentCheck('Verificando autenticidade...');
      const photoOfPhotoScore = detectPhotoOfPhoto(imgData);
      if (photoOfPhotoScore > 0.5) {
        indicators.push({
          type: 'PHOTO_OF_PHOTO',
          confidence: photoOfPhotoScore,
          description: FRAUD_DESCRIPTIONS.PHOTO_OF_PHOTO,
        });
        totalRisk += photoOfPhotoScore * 35;
      }

      // Calculate final risk score (0-100)
      const riskScore = Math.min(Math.round(totalRisk), 100);
      
      // Determine recommendation
      let recommendation: 'APPROVE' | 'REVIEW' | 'REJECT';
      let blocked = false;
      
      if (riskScore > 70) {
        recommendation = 'REJECT';
        blocked = true;
      } else if (riskScore > 40) {
        recommendation = 'REVIEW';
      } else {
        recommendation = 'APPROVE';
      }

      const analysisResult: AntiFraudResult = {
        riskScore,
        indicators,
        blocked,
        recommendation,
      };

      setResult(analysisResult);
      onComplete(analysisResult);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro na análise antifraude';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setAnalyzing(false);
      setCurrentCheck('');
    }
  }, [imageData, detectMoirePattern, analyzeLighting, detectReflections, detectPhotoOfPhoto, onComplete, onError]);

  useEffect(() => {
    analyzeImage();
  }, [analyzeImage]);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg ${className}`}>
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-red-600 text-center">{error}</p>
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
        <Shield className="w-16 h-16 text-blue-600 mb-4 animate-pulse" />
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600 mb-2">Analisando imagem...</p>
        <p className="text-sm text-gray-500">{currentCheck}</p>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Risk Score */}
      <div className={`p-6 rounded-lg mb-4 ${
        result.recommendation === 'APPROVE' 
          ? 'bg-green-50' 
          : result.recommendation === 'REVIEW'
          ? 'bg-yellow-50'
          : 'bg-red-50'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className={`w-8 h-8 ${
              result.recommendation === 'APPROVE' 
                ? 'text-green-600' 
                : result.recommendation === 'REVIEW'
                ? 'text-yellow-600'
                : 'text-red-600'
            }`} />
            <div>
              <h3 className="font-medium text-gray-800">Análise Antifraude</h3>
              <p className="text-sm text-gray-500">Score de Risco</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-3xl font-bold ${
              result.riskScore <= 40 
                ? 'text-green-600' 
                : result.riskScore <= 70
                ? 'text-yellow-600'
                : 'text-red-600'
            }`}>
              {result.riskScore}
            </p>
            <p className="text-sm text-gray-500">de 100</p>
          </div>
        </div>

        {/* Recommendation */}
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          result.recommendation === 'APPROVE' 
            ? 'bg-green-100 text-green-700' 
            : result.recommendation === 'REVIEW'
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-red-100 text-red-700'
        }`}>
          {result.recommendation === 'APPROVE' && (
            <>
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Aprovado - Baixo risco de fraude</span>
            </>
          )}
          {result.recommendation === 'REVIEW' && (
            <>
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Revisão necessária - Risco moderado</span>
            </>
          )}
          {result.recommendation === 'REJECT' && (
            <>
              <XCircle className="w-5 h-5" />
              <span className="font-medium">Rejeitado - Alto risco de fraude</span>
            </>
          )}
        </div>
      </div>

      {/* Fraud Indicators */}
      {result.indicators.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 mb-2">Indicadores Detectados:</h4>
          {result.indicators.map((indicator, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-3 bg-white border rounded-lg"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <span className="text-sm">{indicator.description}</span>
              </div>
              <span className="text-xs text-gray-500">
                {(indicator.confidence * 100).toFixed(0)}% confiança
              </span>
            </div>
          ))}
        </div>
      )}

      {result.indicators.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span>Nenhum indicador de fraude detectado</span>
        </div>
      )}
    </div>
  );
}
